import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
   Box,
   Button,
   FormControl,
   InputLabel,
   MenuItem,
   Select,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   TextField,
   Typography,
} from '@mui/material'

import { InfinityThresholdType } from 'views/Authorized/views/Locations/types'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { MarkupThresholdType } from 'shared/store/useLocations/types'
import { useEditCustomerMutation } from 'shared/store/useCustomers'
import { CustomerType } from 'shared/store/useCustomers/types'
import { useProfileQuery } from 'shared/store/useProfile'
import { useNotificationContext } from 'shared/context/Notifications'

interface Props {
   customerDetails: CustomerType
   thresholds: MarkupThresholdType[]
   priceType: 'GROSS' | 'NET'
   setPriceType: (priceType: 'GROSS' | 'NET') => void
   setThresholds: (thresholds: MarkupThresholdType[]) => void
}

export default function Markup({ customerDetails, thresholds, setThresholds, priceType, setPriceType }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const { data } = useProfileQuery()

   const [infinityThreshold, setInfinityThreshold] = useState<InfinityThresholdType>({
      multiplier: 1,
      addend: 0,
   })
   const { addNotification } = useNotificationContext()
   const { mutate: editCustomer, isLoading: isEditingCustomer } = useEditCustomerMutation()

   const textFieldRef = useRef<HTMLInputElement>(null)

   const priceTypeOptions = [
      { name: t('CUSTOMERS:modal.markups.markupSettings.grossLabel'), value: 'GROSS' },
      { name: t('CUSTOMERS:modal.markups.markupSettings.netLabel'), value: 'NET' },
   ]

   function onSubmit(values: CustomerType) {
      editCustomer(
         {
            payload: {
               ...values,
            },
            customerUuid: values.uuid,
         },
         {
            onSuccess: () => addNotification('successSave', 'success'),
         }
      )
   }

   function addThreshold() {
      const lastElement = thresholds[thresholds.length - 1]
      if (lastElement) {
         const newThresholds = [...thresholds]

         if (lastElement.end) {
            newThresholds.push({
               start: Number((lastElement.end + 0.01).toFixed(2)),
               end: Number((lastElement.end + 0.02).toFixed(2)),
               multiplier: 1,
               addend: 1,
            })
         }
         setThresholds(newThresholds)
      } else {
         setThresholds([{ start: 0, end: null, multiplier: 1, addend: 1 }])
      }
   }

   function handleChange(index: number, field: string, value: number) {
      const newThresholds = thresholds.map((item, i) => {
         if (i === index) {
            return { ...item, [field]: value }
         }
         return item
      })

      setThresholds(newThresholds)
   }

   function getLastThresholdEndValue() {
      const lastElement = thresholds[thresholds.length - 1]
      if (lastElement) {
         const { end } = lastElement
         if (end) {
            return (end + 0.01).toFixed(2)
         }
         return 0
      }
      return 0
   }

   function removeLastItem() {
      const newThresholds = thresholds.slice(0, -1)
      setThresholds(newThresholds)
   }

   function getLastThresholdStartValue() {
      if (thresholds.length > 0) {
         if (thresholds[thresholds.length - 1]) {
            const endValue = thresholds[thresholds.length - 1].end
            if (endValue !== null) {
               return (endValue + 0.01).toFixed(2)
            }
            return ''
         }
      }
      return 0
   }

   useEffect(() => {
      if (customerDetails) {
         setInfinityThreshold({
            multiplier:
               customerDetails.productsMarkupSettings.thresholds[
                  customerDetails.productsMarkupSettings.thresholds.length - 1
               ].multiplier,
            addend:
               customerDetails.productsMarkupSettings.thresholds[
                  customerDetails.productsMarkupSettings.thresholds.length - 1
               ].addend,
         })
      }
   }, [customerDetails])

   return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pt: '24px', alignItems: 'flex-start' }}>
         <FormControl sx={{ mt: '8px' }} component="fieldset" variant="outlined">
            <InputLabel>{t('CUSTOMERS:modal.markups.markupSettings.typeOfPriceLabel')}</InputLabel>
            <Select
               MenuProps={{
                  PaperProps: {
                     style: { maxHeight: '350px' },
                  },
               }}
               value={priceType}
               onChange={(e) => setPriceType(e.target.value)}
               label={t('CUSTOMERS:modal.markups.markupSettings.typeOfPriceLabel')}
            >
               {priceTypeOptions.map((item) => (
                  <MenuItem value={item.value}>{item.name}</MenuItem>
               ))}
            </Select>
         </FormControl>
         <Box sx={{ maxHeight: 450, width: '100%', overflowY: 'scroll' }}>
            <Table stickyHeader>
               <TableHead>
                  <TableRow>
                     <TableCell sx={{ textAlign: 'center' }}>
                        {t('CUSTOMERS:modal.markups.markupSettings.start', {
                           priceType: t(`TRANSLATION:${data?.priceType}`),
                        })}
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center' }}>
                        {t('CUSTOMERS:modal.markups.markupSettings.end', {
                           priceType: t(`TRANSLATION:${data?.priceType}`),
                        })}
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center' }}>
                        {t('CUSTOMERS:modal.markups.markupSettings.multiplier')}
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center' }}>
                        {' '}
                        {t('CUSTOMERS:modal.markups.markupSettings.addend')}
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center' }} />
                  </TableRow>
               </TableHead>
               <TableBody>
                  {thresholds.length > 0 &&
                     thresholds.map((item, index) => {
                        const isLastItem = index === thresholds.length - 1
                        return (
                           // eslint-disable-next-line react/no-array-index-key
                           <TableRow sx={{}} key={index}>
                              <TableCell
                                 sx={{
                                    textAlign: 'center',
                                    padding: { lg: '16px', md: '12px', xs: '8px' },
                                 }}
                              >
                                 <TextField
                                    sx={{
                                       '& .MuiInputBase-input': {
                                          py: 1,
                                          width: { lg: 100, md: 50, xs: 50 },
                                       },
                                    }}
                                    type="number"
                                    variant="outlined"
                                    disabled
                                    value={item.start}
                                    slotProps={{
                                       htmlInput: {
                                          min: item.start,
                                          step: 0.01,
                                       },
                                    }}
                                 />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                                 <TextField
                                    sx={{
                                       '& .MuiInputBase-input': {
                                          py: 1,
                                          width: { lg: 100, md: 50, xs: 50 },
                                       },
                                    }}
                                    type="Number"
                                    inputRef={textFieldRef}
                                    onFocus={(e) => e.target.select()}
                                    variant="outlined"
                                    error={item.end ? item.start > item.end : false}
                                    value={item.end}
                                    onChange={(e) => {
                                       handleChange(index, 'end', Number(e.target.value))
                                    }}
                                    slotProps={{
                                       htmlInput: {
                                          min: item.start + 0.01,
                                          step: 0.01,
                                       },
                                    }}
                                    disabled={!isLastItem}
                                 />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                                 <TextField
                                    sx={{
                                       '& .MuiInputBase-input': {
                                          py: 1,
                                          width: { lg: 100, md: 50, xs: 50 },
                                       },
                                    }}
                                    type="number"
                                    onFocus={(e) => e.target.select()}
                                    variant="outlined"
                                    value={item.multiplier}
                                    onChange={(e) => {
                                       handleChange(index, 'multiplier', Number(e.target.value))
                                    }}
                                    slotProps={{
                                       htmlInput: {
                                          min: 1,
                                          step: 0.01,
                                       },
                                    }}
                                 />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                                 <TextField
                                    sx={{
                                       '& .MuiInputBase-input': {
                                          py: 1,
                                          width: { lg: 100, md: 50, xs: 50 },
                                       },
                                    }}
                                    type="Number"
                                    onFocus={(e) => e.target.select()}
                                    variant="outlined"
                                    value={item.addend}
                                    onChange={(e) => handleChange(index, 'addend', Number(e.target.value))}
                                    inputProps={{
                                       min: 1,
                                       step: 1,
                                    }}
                                 />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                                 {isLastItem && (
                                    <Button onClick={removeLastItem} variant="outlined" color="error">
                                       {t('TRANSLATION:delete')}
                                    </Button>
                                 )}
                              </TableCell>
                           </TableRow>
                        )
                     })}
                  <TableRow>
                     <TableCell
                        sx={{
                           textAlign: 'center',
                           padding: { lg: '16px', md: '12px', xs: '8px' },
                        }}
                     >
                        <TextField
                           sx={{
                              '& .MuiInputBase-input': {
                                 py: 1,
                                 width: { lg: 100, md: 50, xs: 50 },
                              },
                           }}
                           variant="outlined"
                           disabled
                           value={getLastThresholdStartValue()}
                        />
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                        <TextField
                           variant="outlined"
                           sx={{
                              '& .MuiInputBase-input': {
                                 textAlign: 'center',
                                 fontSize: '24px',
                                 paddingY: 0,
                                 width: { lg: 100, md: 50, xs: 50 },
                              },
                           }}
                           value="∞"
                           disabled
                        />
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                        <TextField
                           sx={{
                              '& .MuiInputBase-input': {
                                 py: 1,
                                 width: { lg: 100, md: 50, xs: 50 },
                              },
                           }}
                           type="number"
                           onFocus={(e) => e.target.select()}
                           variant="outlined"
                           value={infinityThreshold.multiplier}
                           onChange={(e) =>
                              setInfinityThreshold({
                                 ...infinityThreshold,
                                 multiplier: Number(e.target.value),
                              })
                           }
                           slotProps={{
                              htmlInput: {
                                 min: 1,
                                 step: 0.1,
                              },
                           }}
                        />
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center', padding: { lg: '16px', md: '12px', xs: '8px' } }}>
                        <TextField
                           sx={{
                              '& .MuiInputBase-input': {
                                 py: 1,
                                 width: { lg: 100, md: 50, xs: 50 },
                              },
                           }}
                           type="Number"
                           onFocus={(e) => e.target.select()}
                           variant="outlined"
                           value={infinityThreshold.addend}
                           onChange={(e) =>
                              setInfinityThreshold({
                                 ...infinityThreshold,
                                 addend: Number(e.target.value),
                              })
                           }
                           slotProps={{
                              htmlInput: {
                                 min: 0,
                                 step: 1,
                              },
                           }}
                        />
                     </TableCell>
                     <TableCell />
                  </TableRow>
               </TableBody>
            </Table>
         </Box>
         {thresholds.find((item) => item.end && item.start > item.end) && (
            <Typography variant="body1" color="error">
               {t('CUSTOMERS:modal.markups.markupSettings.badEndValue', {
                  thresholdIndex: thresholds.findIndex((item) => item.end && item.start > item.end) + 1,
               })}
            </Typography>
         )}
         {thresholds.length === 20 && (
            <Typography variant="body1" color="error">
               {t('CUSTOMERS:modal.markups.markupSettings.maxCountOfThresholds')}
            </Typography>
         )}
         {(thresholds.find((item) => item.multiplier < 1) || infinityThreshold.multiplier < 1) && (
            <Typography variant="body1" color="error">
               {t('CUSTOMERS:modal.markups.markupSettings.badMultiplierValue')}
            </Typography>
         )}
         {thresholds.length > 0 && thresholds[thresholds.length - 1].end === null && (
            <Typography variant="body1" color="error">
               {t('CUSTOMERS:modal.markups.markupSettings.tooltip')}
            </Typography>
         )}
         <Box sx={{ textAlign: 'end', py: 1 }}>
            <OutlinedButton
               disabled={
                  (thresholds.length > 0 && !thresholds[thresholds.length - 1].end) ||
                  thresholds.length === 20 ||
                  (thresholds.length > 0 && !!thresholds.find((item) => item.end && item.start > item.end)) ||
                  (thresholds.length > 0 && !!thresholds.find((item) => item.multiplier < 1))
               }
               variant="outlined"
               onClick={addThreshold}
            >
               {t('CUSTOMERS:modal.markups.markupSettings.addThreshold')}
            </OutlinedButton>
         </Box>

         <ContainedButton
            variant="contained"
            sx={{ m: '0 auto' }}
            disabled={
               isEditingCustomer ||
               (thresholds.length > 0 && !thresholds[thresholds.length - 1].end) ||
               thresholds.length === 20 ||
               (thresholds.length > 0 && !!thresholds.find((item) => item.end && item.start > item.end)) ||
               (thresholds.length > 0 && !!thresholds.find((item) => item.multiplier < 1))
            }
            onClick={() =>
               onSubmit({
                  ...customerDetails,
                  productsMarkupSettings: {
                     priceType,
                     thresholds: [
                        ...thresholds,
                        {
                           ...infinityThreshold,
                           end: null,
                           start: Number(getLastThresholdEndValue()),
                        },
                     ],
                  },
               })
            }
         >
            {t('TRANSLATION:save')}
         </ContainedButton>
      </Box>
   )
}
